import { useCallback, useEffect, useRef, useState } from "react";

import { useAccountLinkAnalytics } from "../../../lib/accounts-link/useAccountLinkAnalytics";
import type {
  FakeDoorMmfaStep,
  FakeDoorMmfaVariant,
  StepAction,
  StepConfig,
} from "./types";

export const useFakeDoorMmfa = ({
  steps,
  thankYouStep,
  variant,
  onExit,
}: {
  steps: StepConfig[];
  thankYouStep: StepConfig;
  variant: FakeDoorMmfaVariant;
  onExit: () => void;
}) => {
  const [index, setIndex] = useState(0);
  const [inThankYou, setInThankYou] = useState(false);
  const { trackEvent } = useAccountLinkAnalytics();

  const currentStep = inThankYou ? thankYouStep : steps[index]!;
  const progress = inThankYou
    ? 100
    : Math.round(((index + 1) / steps.length) * 100);
  const canGoBack = !inThankYou && index > 0;

  const viewedStepsRef = useRef(new Set<FakeDoorMmfaStep>());
  const terminalFiredRef = useRef(false);
  const currentStepIdRef = useRef<FakeDoorMmfaStep>(currentStep.id);

  useEffect(() => {
    currentStepIdRef.current = currentStep.id;
  }, [currentStep.id]);

  useEffect(() => {
    const stepId = currentStep.id;
    if (stepId === "thank_you") {
      return;
    }
    if (!viewedStepsRef.current.has(stepId)) {
      viewedStepsRef.current.add(stepId);
      trackEvent("FakeDoorMmfaViewed", { variant, step: stepId });
    }
  }, [currentStep.id, variant, trackEvent]);

  // Fires FakeDoorMmfaDismissed when the flow unmounts without hitting a
  // terminal action — e.g. the user swipes the outer accounts-link modal
  // closed instead of using the in-flow close button.
  useEffect(
    () => () => {
      if (terminalFiredRef.current) {
        return;
      }
      const step = currentStepIdRef.current;
      if (step === "thank_you") {
        return;
      }
      trackEvent("FakeDoorMmfaDismissed", { variant, step });
    },
    [variant, trackEvent],
  );

  const runAction = useCallback(
    (action: StepAction) => {
      const step = currentStep.id;
      if (action === "continue") {
        if (step !== "waitlist" && step !== "thank_you") {
          trackEvent("FakeDoorMmfaContinued", { variant, step });
        }
        if (index + 1 < steps.length) {
          setIndex(index + 1);
        }
        return;
      }
      if (action === "skip") {
        if (step === "claim_monitoring") {
          trackEvent("FakeDoorMmfaSkipped", { variant, step });
          trackEvent("FakeDoorMmfaCompleted", { variant });
          terminalFiredRef.current = true;
          setInThankYou(true);
          return;
        }
        if (step === "email_action") {
          trackEvent("FakeDoorMmfaSkipped", { variant, step });
        }
        terminalFiredRef.current = true;
        onExit();
        return;
      }
      if (action === "done") {
        trackEvent("FakeDoorMmfaCompleted", { variant });
        terminalFiredRef.current = true;
        onExit();
      }
    },
    [index, steps.length, variant, currentStep.id, trackEvent, onExit],
  );

  const handleBack = useCallback(() => {
    if (index > 0) {
      setIndex(index - 1);
    }
  }, [index]);

  const handleDismiss = useCallback(() => {
    const step = currentStep.id;
    if (step !== "thank_you") {
      trackEvent("FakeDoorMmfaDismissed", { variant, step });
    }
    terminalFiredRef.current = true;
    onExit();
  }, [currentStep.id, variant, trackEvent, onExit]);

  return {
    currentStep,
    progress,
    canGoBack,
    runAction,
    handleBack,
    handleDismiss,
  };
};
